import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

// Desktop collapses the sidebar into an icon rail; at ≤850px it becomes a drawer.
// Focus moves to the control that replaces the one just activated.
export default function useStudioSidebar(signed) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 850px)').matches);
  const [sidebarWidth, setSidebarWidth] = useState(() => window.matchMedia('(max-width: 1100px)').matches ? 208 : 232);
  const openRef = useRef(null);
  const closeRef = useRef(null);
  const reduced = useReducedMotion();
  // Stable, like the handlers below: the sidebar, the rail and the header are memoized on them.
  const transition = useMemo(() => ({ duration: reduced ? 0 : 0.24, ease: [0.25, 0.1, 0.25, 1] }), [reduced]);
  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 850px)');
    const narrowQuery = window.matchMedia('(max-width: 1100px)');
    const update = () => {
      setMobile(mobileQuery.matches);
      setSidebarWidth(narrowQuery.matches ? 208 : 232);
      if (!mobileQuery.matches) setMobileOpen(false);
    };
    mobileQuery.addEventListener('change', update);
    narrowQuery.addEventListener('change', update);
    return () => { mobileQuery.removeEventListener('change', update); narrowQuery.removeEventListener('change', update); };
  }, []);
  const nav = useCallback(fn => { fn(); setMobileOpen(false); }, []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const closeSidebar = useCallback(() => {
    if (mobile) setMobileOpen(false);
    else { setSidebarOpen(false); requestAnimationFrame(() => openRef.current?.focus()); }
  }, [mobile]);
  const openSidebar = useCallback(() => {
    if (mobile) setMobileOpen(true);
    else { setSidebarOpen(true); requestAnimationFrame(() => closeRef.current?.focus()); }
  }, [mobile]);
  // Folding asked by a screen (a preview opening): no focus moves, phones keep their drawer shut.
  const collapse = useCallback(() => { setSidebarOpen(false); setMobileOpen(false); }, []);
  const inset = signed && !mobile ? (sidebarOpen ? sidebarWidth : 64) : 0;
  return { mobile, mobileOpen, sidebarOpen, sidebarWidth, inset, transition, openRef, closeRef, nav, closeMobile, openSidebar, closeSidebar, collapse };
}
